import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

export interface OperationItem {
  origin?: "user" | "backoffice_manual";
  id: number;
  publicId?: string | null;
  userId: number;
  type: string;
  reasonCode: string | null;
  statusCode: string;
  amount: number;
  currency: string;
  externalRef: string | null;
  created: string;
  user: {
    id: number;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  };
}

export interface ProviderOutcome {
  operationType?: string | null;
  statusCode?: string | null;
  reasonCode?: string | null;
  providerCode?: string | null;
  providerMessage?: string | null;
  providerStatus?: string | null;
  terminalStatusCode?: string | null;
  externalRef?: string | null;
  providerPaymentId?: string | null;
  providerTransactionId?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  depositId?: string | number | null;
  withdrawalId?: string | number | null;
  amountRequested?: string | number | null;
  amountProcessed?: string | number | null;
  currency?: string | null;
}

export interface OperationDetails extends OperationItem {
  publicId?: string | null;
  referenceType?: string | null;
  referenceId?: string | number | null;
  methodCode?: string | null;
  processingType?: string | null;
  completionMode?: string | null;
  completed?: string | null;
  providerOutcome?: ProviderOutcome | null;
}

export interface LedgerItem {
  id: number;
  accountLabel: string;
  effectLabel: string;
  amount: number;
  currency: string;
  balanceAfter: number;
  created: string;
  user?: {
    email?: string;
    firstName?: string;
  } | null;
}

interface PaymentsOperationsState {
  list: OperationItem[];
  operationDetails: OperationDetails | null;
  ledgerItems: LedgerItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  selectedItems: number[];
  loadingDetails: boolean;
}

interface GetListPayload {
  limit: number;
  offset: number;
  userIds?: number[];
  currencies?: string[];
  operationTypes?: string[];
  operationReasonCodes?: string[];
  operationStatusCodes?: string[];
  operationId?: number[];
  order: {
    id?: string;
    amount?: string;
    currency?: string;
    statusCode?: string;
    type?: string;
  };
}

export const getOperationsList = createAsyncThunk(
  "paymentsOperations/list",
  async (data: GetListPayload) => {
    const result = await instance.post(apiRoutes.paymentOperationsList, data);

    return result.data;
  },
);

const initialState: PaymentsOperationsState = {
  list: [],
  operationDetails: null,
  ledgerItems: [],
  isListFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  loadingDetails: false,
};
export const getOperationDetails = createAsyncThunk(
  "paymentsOperations/details",
  async (id: number) => {
    const result = await instance.get(`/payment/operations/${id}`);

    return result.data;
  },
);

export const paymentsOperationsSlice = createSlice({
  name: "paymentsOperations",
  initialState,
  reducers: {
    handlePaginate: (
      state: PaymentsOperationsState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },

    handleSelectItem: (
      state: PaymentsOperationsState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },

    handleSelectAll: (state: PaymentsOperationsState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getOperationsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getOperationDetails.pending, (state) => {
      state.loadingDetails = true;
      state.operationDetails = null;
    });
    builder.addCase(getOperationDetails.fulfilled, (state, action) => {
      state.loadingDetails = false;

      if (!action.payload) return;

      state.operationDetails = action.payload.details;
      state.ledgerItems = action.payload.ledgerItems;
    });

    builder.addCase(getOperationDetails.rejected, (state) => {
      state.loadingDetails = false;
    });
  },
});

export const { handlePaginate, handleSelectAll, handleSelectItem } =
  paymentsOperationsSlice.actions;
